/**
 * How many entries the cinema website's bottom navigation fits before labels start colliding,
 * measured against the longest default labels ("Gutscheine", "Aktuelles") at the narrowest
 * common phone width.
 */
export const MAX_MOBILE_NAV_ITEMS = 5;

/** Sections that can occupy a slot in the mobile bottom navigation, in render order. */
export type MobileNavigationSections = {
    home: boolean;
    program: boolean;
    specials: boolean;
    deals: boolean;
    shops: boolean;
    announcements: boolean;
    bar: boolean;
    info: boolean;
};

export type MobileNavigationMoreSection = 'announcements' | 'bar' | 'info';

export type MobileNavigationPlan = {
    /** Specials is folded into a dropdown behind the program entry. */
    collapseProgram: boolean;
    /** Announcements / bar / info are folded into a "more" dropdown. */
    collapseMore: boolean;
    /** The sections that end up inside that dropdown — empty when it is not used. */
    moreSections: MobileNavigationMoreSection[];
};

/**
 * Decides how the bottom navigation folds itself to stay within MAX_MOBILE_NAV_ITEMS.
 *
 * Folding is applied only while the row is still over budget, and only where it actually frees
 * a slot — so a cinema with few entries keeps every one of them directly tappable:
 *
 *   1. Specials folds into the program entry, which becomes a dropdown.
 *   2. Announcements, bar and info fold together into a "more" dropdown.
 *
 * Shared so the website and the cinema app's navigation settings cannot disagree about what a
 * given configuration will look like.
 */
export function planMobileNavigation(sections: MobileNavigationSections): MobileNavigationPlan {
    const visibleCount = Object.values(sections).filter(Boolean).length;

    // Folding specials into the program dropdown only pays off when both would be shown.
    const collapseProgram = visibleCount > MAX_MOBILE_NAV_ITEMS && sections.program && sections.specials;
    const countAfterProgram = collapseProgram ? visibleCount - 1 : visibleCount;

    // Likewise, grouping is only worth a slot when there are at least two entries to group.
    const moreCandidates: MobileNavigationMoreSection[] = (
        ['announcements', 'bar', 'info'] as MobileNavigationMoreSection[]
    ).filter((section) => sections[section]);
    const collapseMore = countAfterProgram > MAX_MOBILE_NAV_ITEMS && moreCandidates.length > 1;

    return {
        collapseProgram,
        collapseMore,
        moreSections: collapseMore ? moreCandidates : []
    };
}
